/*
rust code:

 #[derive(Deserialize)]
#[serde(tag = "type")]
pub enum Request {
    SitesAndLocations {},

    #[serde(rename_all = "camelCase")]
    Phone {
        number: String,
    },

    #[serde(rename_all = "camelCase")]
    Email {
        address: String,
    },

    #[serde(rename_all = "camelCase")]
    Column {
        site_id: String,

        location_id: Option<String>,

        // 1. "{column_name}:{locker_number}" (e.g., "Column1:3")
        // 2. "{locker_name}" (e.g., "AB01_1")
        search_string: String,
    },
}

SitesAndLocationsResponse
        return Ok(warp::reply::json(&DataResponse {
            data: Some(sites),
            extra: Some(locations),
        }));

SessionsResponse
        return Ok(warp::reply::json(&DataResponse {
            data: Some(sessions),
            extra: None::<Empty>,
        }));
*/

import {
  Site,
  Location,
  Session,
  Locker,
  Column,
  Factory,
} from 'src/app/_models'

export enum SpotlightRequestType {
  Phone = 'Phone',
  Email = 'Email',
  Column = 'Column',
  SitesAndLocations = 'SitesAndLocations',
}

export class SpotlightRequest {
  public static getFactory(): Factory<SpotlightRequest> {
    return new (class implements Factory<SpotlightRequest> {
      make(string: string): SpotlightRequest {
        if (this.isStringAPhoneNumber(string)) {
          return new SpotlightPhoneRequest(string)
        } else if (this.isStringAnEmail(string)) {
          return new SpotlightEmailRequest(string)
        } else if (this.isStringAColumnRequest(string)) {
          return new SpotlightColumnRequest(string)
        }
        throw new Error('Unrecognized SpotlightRequest type (' + string + ').')
      }

      getTableName(): string {
        throw new Error('SpotlightRequest has no table')
      }

      isStringAPhoneNumber(string: string): boolean {
        const stringVector: string[] = string.split('')
        let isPhoneNumber = true
        if (!string.length) {
          return false
        }

        if (stringVector[0] === '+') {
          for (let i = 1; i < stringVector.length; i++) {
            if (!/^\d$/.test(stringVector[i])) {
              isPhoneNumber = false
              break
            }
          }
        } else {
          for (let i = 0; i < stringVector.length; i++) {
            if (!/^\d$/.test(stringVector[i])) {
              isPhoneNumber = false
              break
            }
          }
        }

        return isPhoneNumber
      }

      isStringAnEmail(string: string): boolean {
        if (!string || string.length === 0 || string.length > 254) {
          return false
        }

        const atCount = (string.match(/@/g) || []).length
        if (atCount !== 1) {
          return false
        }

        const [localPart, domainPart] = string.split('@')

        if (!localPart || localPart.length === 0) {
          return false
        }

        if (!domainPart || domainPart.length === 0) {
          return false
        }

        if (!domainPart.includes('.')) {
          return false
        }

        const simpleEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return simpleEmailRegex.test(string)
      }

      isStringAColumnRequest(string: string): boolean {
        if (string.includes('/')) {
          return true
        }
        return false
      }
    })()
  }
}

export class SpotlightSitesAndLocationsRequest extends SpotlightRequest {
  type: SpotlightRequestType = SpotlightRequestType.SitesAndLocations
}

export class SpotlightColumnRequest extends SpotlightRequest {
  type: SpotlightRequestType = SpotlightRequestType.Column
  siteId: string
  locationId?: string
  searchString: string

  constructor(string: string) {
    super()
    const [siteId, locationId, searchString] = string.split('/')
    const locationIdParsed = locationId.includes('?') ? undefined : locationId
    this.siteId = siteId
    this.locationId = locationIdParsed
    this.searchString = searchString.trim().toLowerCase()
  }
}

export class SpotlightPhoneRequest extends SpotlightRequest {
  type: SpotlightRequestType = SpotlightRequestType.Phone
  number: string

  constructor(string: string) {
    super()
    this.number = string.replace('+', '').trim().toLowerCase()
  }
}

export class SpotlightEmailRequest extends SpotlightRequest {
  type: SpotlightRequestType = SpotlightRequestType.Email
  address: string

  constructor(string: string) {
    super()
    this.address = string.trim().toLowerCase()
  }
}

export class SpotlightSitesAndLocationsResponse {
  constructor(
    public data: Site[],
    public extra: Location[]
  ) {}
}

export class SpotlightUserResponse {
  constructor(public data: Session[]) {}
}

export class SpotlightColumnSessionsResponse {
  constructor(public data: { column: Column; sessions: LockerSessions[] }) {}
}

export class LockerSessions {
  constructor(
    public locker: Locker,
    public sessions: Session[]
  ) {}
}

export type SpotlightRequestTypes =
  | SpotlightPhoneRequest
  | SpotlightEmailRequest
  | SpotlightColumnRequest
